"use client";

import { medimoPublicQC } from "@medimo/core-api/internal/next";
import { RequestResetPasswordDialog, SignInForm } from "@medimo/core-auth/ui";
import { useMicrophoneBannerDismissal } from "@medimo/global-state";
import { NOTIFICATION_BANNER_DISMISSED_SESSION_KEY } from "@medimo/notification";
import { Card, CardContent, CardHeader, CardTitle } from "@medimo/ui";
import { signOut } from "aws-amplify/auth";
import { LoaderIcon } from "lucide-react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useIsClient } from "usehooks-ts";
import {
  clearParentDomainAmplifyCookies,
  consumeCollaboLinkAbortCookie,
} from "../_hooks/amplify-cookie-utils";
import {
  COLLABO_LINK_ABORT_MESSAGES,
  clearCollaboLinkMode,
  isCollaboLinkAbortReason,
  readCollaboPendingInfo,
  useCollaboLinkFlow,
  writeCollaboPendingInfo,
} from "../_hooks/use-collabo-link-flow";
import { useCreateUserOnDB } from "../_hooks/use-create-user";
import { CollaboLinkPanel, CollaboSignupGuide } from "../collabo-link-panel";
import {
  clearCollaboLinkReturn,
  saveCollaboLinkReturn,
} from "../collabo-link-return";
import { getCollaboPortalPending } from "../collabo-portal";

export default function SignInPage() {
  const router = useRouter();
  const isClient = useIsClient();
  const searchParams = useSearchParams();
  const isCollaboLinkMode = searchParams.get("collabo_flow") === "portal_link";
  // Fetched from GET /v3/collabo/portal/pending (cookie-authenticated), never from the URL —
  // Datadog RUM / PostHog capture page-view URLs, which would leak this PII to those
  // external SaaS tools.
  const [collaboName, setCollaboName] = useState<string | null>(null);
  const [collaboEmail, setCollaboEmail] = useState<string | null>(null);
  const collaboLinkAbortReason = searchParams.get("collabo_link");
  // Shown in the form's error slot (below the password field) like every other sign-in error,
  // and hidden once the user submits so it never stacks with the new attempt's error.
  const [dismissedAbortReason, setDismissedAbortReason] = useState<
    string | null
  >(null);
  const collaboLinkAbortMessage =
    isCollaboLinkAbortReason(collaboLinkAbortReason) &&
    collaboLinkAbortReason !== dismissedAbortReason
      ? COLLABO_LINK_ABORT_MESSAGES[collaboLinkAbortReason]
      : null;
  const dismissAbortMessage = () =>
    setDismissedAbortReason(collaboLinkAbortReason);
  const {
    completeLink,
    isLinking,
    enterNewPasswordStep,
    enterTwoFactorStep,
    leaveOnLinkRefMismatch,
  } = useCollaboLinkFlow();
  const {
    isPending,
    error,
    mutateAsync: registerUserOrUpdateOnDB,
  } = useCreateUserOnDB();
  const { mutateAsync: requestPasswordRecovery } = medimoPublicQC.useMutation(
    "post",
    "/v2/auth/password-recovery",
  );

  // Collabo auto-login failure while the user has an active medimo session: sign out so the
  // error message + login form are coherent.  The middleware let us through despite the live
  // session specifically so we can show the collabo_link abort reason.  Only an abort the link
  // flow marked signs out: a crafted ?collabo_link= link must not log the user out.
  // signOut() leaves the parent-domain session written by Collabo SSO, which the middleware
  // would still treat as signed in, so it is cleared as well.
  // LINK_CONFLICT is excluded: the session in the browser belongs to the account that won the
  // race in another tab — signing out here would destroy a valid session this tab did not create.
  // PENDING_CHANGED likewise: any session in the browser comes from the other tab's link.
  useEffect(() => {
    if (
      isCollaboLinkAbortReason(collaboLinkAbortReason) &&
      consumeCollaboLinkAbortCookie() &&
      collaboLinkAbortReason !== "link_conflict" &&
      collaboLinkAbortReason !== "pending_changed"
    ) {
      signOut()
        .catch(() => undefined)
        .finally(() => clearParentDomainAmplifyCookies());
    }
  }, [collaboLinkAbortReason]);

  // Reaching sign-in means any earlier link detour has ended: a return key written by the reset
  // dialog is only produced after this mount, and the mode flag is re-set by the next submit.
  useEffect(() => {
    clearCollaboLinkReturn();
    clearCollaboLinkMode();
  }, []);

  // Link-mode display name / email pre-fill: fetched once on mount, not read from the URL.
  // Held back behind isPendingInfoLoading (not just set when it resolves) because
  // SignInForm's react-hook-form only reads defaultValues at mount — populating the state
  // after the form already rendered would never reach the email field.
  //
  // GET pending is always tried first; a success overwrites the sessionStorage cache so a
  // fresh/different pending (e.g. after an OIDC restart) is never shadowed by stale data. The
  // cache is only read as a fallback when the endpoint fails (pending TTL elapsed, network)
  // — e.g. the user left this screen open past the TTL and then reloaded. If neither
  // gives a result, the fields just stay empty; the real check happens again on POST /link
  // (returns 410).
  const [isPendingInfoLoading, setIsPendingInfoLoading] =
    useState(isCollaboLinkMode);
  useEffect(() => {
    if (!isCollaboLinkMode) return;
    const cancelled = { current: false };
    getCollaboPortalPending().then(({ info: pending, linkRef }) => {
      if (cancelled.current) return;
      // Checked before anything is shown: if another tab replaced the pending with a different
      // Collabo ID, the form must not appear with that ID's name.
      if (leaveOnLinkRefMismatch(linkRef)) return;
      const info = pending ?? readCollaboPendingInfo();
      if (info) {
        setCollaboName(info.name);
        setCollaboEmail(info.email);
      }
      if (pending) {
        writeCollaboPendingInfo(pending);
      }
      setIsPendingInfoLoading(false);
    });
    return () => {
      cancelled.current = true;
    };
  }, [isCollaboLinkMode, leaveOnLinkRefMismatch]);

  if (!isClient) return null;

  if (isCollaboLinkMode && isPendingInfoLoading) {
    return <LoaderIcon className="animate-spin" />;
  }

  return (
    <>
      <Card className="mx-3 my-5 w-full max-w-md py-5">
        <CardHeader className="space-y-1">
          <Image
            src="/medimo/logo-with-text.png"
            alt="logo"
            width={300}
            height={100}
            className="mx-auto w-3/4 pb-10"
            priority={false}
          />
          {isCollaboLinkMode && <CollaboLinkPanel collaboName={collaboName} />}
          <CardTitle className="text-xl">ログイン</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          {isCollaboLinkMode ? (
            <SignInForm
              isPending={isLinking}
              defaultValues={{
                // Pre-filled but editable: the medimo account may use a different address.
                email: collaboEmail ?? "",
                password: "",
              }}
              errorMessage={collaboLinkAbortMessage}
              // Amplify only writes host-only cookies, so a previous user's parent-domain
              // session left by the Collabo flow could otherwise shadow this sign-in.
              onSignInSubmit={() => {
                dismissAbortMessage();
                clearParentDomainAmplifyCookies();
              }}
              // signIn finished with no further challenge: an existing user. Finalize the link.
              onSuccessToSignIn={() => {
                setDismissedAbortReason(null);
                completeLink();
              }}
              onRequireNewPassword={(result) =>
                enterNewPasswordStep(result.email)
              }
              onRequireTwoFactor={() => enterTwoFactorStep()}
            />
          ) : (
            <SignInForm
              isPending={isPending}
              defaultValues={{
                email: "",
                password: "",
              }}
              // Amplify only writes host-only cookies, so a previous user's parent-domain
              // session left by the Collabo flow could otherwise shadow this sign-in.
              onSignInSubmit={() => {
                dismissAbortMessage();
                clearParentDomainAmplifyCookies();
              }}
              onSuccessToSignIn={() => {
                registerUserOrUpdateOnDB()
                  .then(() => {
                    useMicrophoneBannerDismissal.getState().reset();
                    try {
                      sessionStorage.setItem("showNotificationDialog", "true");
                      // 同じタブでも次回ログインではお知らせバナーを再表示する
                      sessionStorage.removeItem(
                        NOTIFICATION_BANNER_DISMISSED_SESSION_KEY,
                      );
                    } catch (e) {
                      console.error(
                        "セッションストレージへの書き込みに失敗しました:",
                        e,
                      );
                    }

                    router.replace("/");
                  })
                  .catch(async () => {
                    await signOut().catch(() => undefined);
                  });
              }}
              onRequireNewPassword={(result) => {
                router.replace(
                  `/auth/set-initial-password?email=${encodeURIComponent(result.email)}`,
                );
              }}
              onRequireTwoFactor={() => {
                router.replace("/auth/two-factor");
              }}
              errorMessage={error?.message ?? collaboLinkAbortMessage}
            />
          )}
          <RequestResetPasswordDialog
            logoSrc="/medimo/logo-with-text.png"
            onRequestRecovery={async (email: string) => {
              await requestPasswordRecovery({ body: { email } });
              if (isCollaboLinkMode) {
                saveCollaboLinkReturn(searchParams);
                clearCollaboLinkMode();
              }
              router.replace(
                `/auth/reset-password?email=${encodeURIComponent(email)}`,
              );
            }}
          />
          {isCollaboLinkMode && <CollaboSignupGuide />}
        </CardContent>
      </Card>
    </>
  );
}
